import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share, Plus, Edit2, MoreVertical } from 'lucide-react';
import api, { wantToTryAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../analytics';
import formatImageSrc from '../lib/formatImageSrc';
import ProgressTracker from '../components/ProgressTracker';
import AddToListModal from '../components/modals/AddToListModal';
import ShareModal from '../components/modals/ShareModal';
import EditReviewModal from '../components/modals/EditReviewModal';
import Fab from '../components/Fab';
import OnboardingWizardModal from '../components/onboarding/OnboardingWizardModal';
import WantToTryButton from '../components/WantToTryButton';
import { shouldShowWizard } from '../lib/onboardingStorage';
import useWichListFeedback from '../hooks/useWichListFeedback';
import { fetchTotals } from '../lib/stats';
import { getNextUserMilestone, getUserProgressPercent } from '../lib/userProgress';
import { useWichList } from '../hooks/useWichList';
import { StarRating, formatRating } from '../components/StarRating';

// API base is centralized in lib/api

const TopReviewsCarousel = React.lazy(() => import('../components/TopReviewsCarousel'));

const TopReviewsCarouselSkeleton = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" aria-label="Trending sandwiches loading">
    <div className="h-8 w-56 bg-white/70 rounded-xl animate-pulse mb-6" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, idx) => (
        <div key={idx} className="bg-white/80 rounded-lg shadow-sm p-4 animate-pulse">
          <div className="aspect-[4/3] bg-gray-200 rounded-md mb-4" />
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
        </div>
      ))}
    </div>
  </section>
);

const Home = () => {
  const [feed, setFeed] = useState([]);
  const [feedCursor, setFeedCursor] = useState(null);
  const [feedHasMore, setFeedHasMore] = useState(false);
  const [feedLoadingMore, setFeedLoadingMore] = useState(false);
  const [topReviews, setTopReviews] = useState([]);
  const [topReviewsLoading, setTopReviewsLoading] = useState(false);
  const [featuredReview, setFeaturedReview] = useState(null);
  const [featuredReviewLoading, setFeaturedReviewLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [commentThreads, setCommentThreads] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [commentSubmitting, setCommentSubmitting] = useState({});
  const [showAddToListModal, setShowAddToListModal] = useState(false);
  const [selectedSandwich, setSelectedSandwich] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [reviewToEdit, setReviewToEdit] = useState(null);
  const [showListMenu, setShowListMenu] = useState(null);
  const [followingMap, setFollowingMap] = useState({});
  const [followLoading, setFollowLoading] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [userStatsLoading, setUserStatsLoading] = useState(false);
  const [wichListShelf, setWichListShelf] = useState([]);
  const [wichListShelfLoading, setWichListShelfLoading] = useState(false);
  const [highlightWichListReviews, setHighlightWichListReviews] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [overflowingReviews, setOverflowingReviews] = useState({});
  const [likeAnimating, setLikeAnimating] = useState({});
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const [featuredOverflowing, setFeaturedOverflowing] = useState(false);
  const reviewTextRefs = useRef({});
  const featuredTextRef = useRef(null);
  const homeRequestIdRef = useRef(0);
  const deferredWorkTimeoutRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isGuest = !user;
  const { trackLike, trackComment, trackCommentDeleted, trackSandwichView, trackFollow } = useAnalytics();
  const showWichListFeedback = useWichListFeedback();
  const { isInWichList, ensureMembership } = useWichList();
  const personalReviewCount = userStats?.reviews_count ?? 0;
  const highlightTimeoutRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const feedCursorRef = useRef(null);
  const feedHasMoreRef = useRef(false);
  const feedLoadingMoreRef = useRef(false);
  const wichListReviewCount = user
    ? feed.filter((item) => isInWichList(item.sandwich.id)).length
    : 0;

  // Function to scroll to feed section
  const scrollToFeed = () => {
    const feedSection = document.getElementById('feed-section');
    if (feedSection) {
      feedSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const handleExploreClick = () => {
    if (user) {
      scrollToFeed();
    } else {
      navigate('/login');
    }
  };

  const handleActivityStripClick = () => {
    if (!user) return;
    scrollToFeed();
    setHighlightWichListReviews(true);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightWichListReviews(false);
      highlightTimeoutRef.current = null;
    }, 3000);
  };

  // Helper function to format counts (999 -> 1k)
  const formatCount = (count) => {
    if (count >= 1000) {
      return Math.floor(count / 1000) + 'k';
    }
    return count.toString();
  };

  useEffect(() => {
    if (user) {
      fetchFeed({ showLoading: true });
      // Check if we should show onboarding wizard
      if (shouldShowWizard()) {
        // Small delay to let the page load first
        const timer = setTimeout(() => {
          setShowOnboarding(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    } else {
      // Cancel any in-flight home fetches / deferred work
      homeRequestIdRef.current += 1;
      if (deferredWorkTimeoutRef.current) {
        clearTimeout(deferredWorkTimeoutRef.current);
        deferredWorkTimeoutRef.current = null;
      }
      setFeed([]);
      setFeedCursor(null);
      setFeedHasMore(false);
      feedCursorRef.current = null;
      feedHasMoreRef.current = false;
      feedLoadingMoreRef.current = false;
      setFeedLoadingMore(false);
      setTopReviews([]);
      setTopReviewsLoading(false);
      setFeaturedReview(null);
      setFeaturedReviewLoading(false);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (deferredWorkTimeoutRef.current) {
        clearTimeout(deferredWorkTimeoutRef.current);
        deferredWorkTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadShelf = async () => {
      if (!user) {
        setWichListShelf([]);
        return;
      }
      try {
        setWichListShelfLoading(true);
        const response = await wantToTryAPI.getList({ limit: 10, sortBy: 'recent' });
        if (isMounted) {
          setWichListShelf(response.sandwiches || []);
        }
      } catch (error) {
        if (isMounted) {
          setWichListShelf([]);
        }
      } finally {
        if (isMounted) {
          setWichListShelfLoading(false);
        }
      }
    };
    loadShelf();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    const loadUserStats = async () => {
      if (!user) {
        setUserStats(null);
        return;
      }
      try {
        setUserStatsLoading(true);
        const totals = await fetchTotals();
        if (isMounted) {
          setUserStats(totals.user || null);
        }
      } catch (error) {
        if (isMounted) {
          setUserStats(null);
        }
      } finally {
        if (isMounted) {
          setUserStatsLoading(false);
        }
      }
    };
    loadUserStats();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Fetch comments for a specific review
  const fetchComments = async (reviewId) => {
    try {
      setCommentLoading(prev => ({ ...prev, [reviewId]: true }));
      const response = await api.get(`/comments/${reviewId}`);
      setCommentThreads(prev => ({ ...prev, [reviewId]: response.data.comments || [] }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      setCommentThreads(prev => ({ ...prev, [reviewId]: [] }));
    } finally {
      setCommentLoading(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  // Toggle comment thread visibility
  const toggleCommentThread = (reviewId) => {
    if (commentThreads[reviewId]) {
      // If thread is already loaded, hide it
      setCommentThreads(prev => {
        const newState = { ...prev };
        delete newState[reviewId];
        return newState;
      });
    } else {
      // If thread is not loaded, show it and fetch comments
      setCommentThreads(prev => ({ ...prev, [reviewId]: [] }));
      fetchComments(reviewId);
    }
  };

  // Submit a new comment
  const submitComment = async (reviewId) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    const text = commentText[reviewId]?.trim();
    if (!text) return;

    try {
      setCommentSubmitting(prev => ({ ...prev, [reviewId]: true }));
      await api.post(
        '/comment',
        {
          review_id: reviewId,
          comment_text: text
        }
      );

      // Clear the input
      setCommentText(prev => ({ ...prev, [reviewId]: '' }));
      
      // Refresh comments
      await fetchComments(reviewId);
      
      // Track the comment
      trackComment(reviewId);
      
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to post comment. Please try again.');
    } finally {
      setCommentSubmitting(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  const handleDeleteComment = async (reviewId, commentId) => {
    if (!user) return;
    const confirmed = window.confirm("Delete this comment? This can’t be undone.");
    if (!confirmed) return;
    try {
      await api.delete(`/comment/${commentId}`);
      // Refresh comments thread
      await fetchComments(reviewId);
      // Optimistically decrement comments_count for this feed item
      setFeed((prev) =>
        prev.map((it) => {
          if (it.review_id !== reviewId) return it;
          const next = Math.max(0, (it.comments_count || 0) - 1);
          return { ...it, comments_count: next };
        })
      );
      trackCommentDeleted(reviewId, commentId);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  // Handle comment text change
  const handleCommentTextChange = (reviewId, value) => {
    setCommentText(prev => ({ ...prev, [reviewId]: value }));
  };

  // Batch fetch following status for all users in the feed
  const fetchBatchFollowingStatus = async (feedData, options = {}) => {
    const merge = !!options.merge;
    const requestId = options.requestId ?? null;
    if (!user || !feedData.length) return;

    try {
      const uniqueUserIds = [...new Set(feedData.map(item => item.user.id))]
        .filter(userId => userId !== user.id); // Exclude current user
      
      let userIdsToFetch = uniqueUserIds;
      if (merge) {
        userIdsToFetch = userIdsToFetch.filter((userId) => !(userId in followingMap));
      }

      if (userIdsToFetch.length === 0) return;

      const response = await api.get(`/following/batch?user_ids=${userIdsToFetch.join(',')}`);
      const incoming = response.data.following_status || {};

      if (requestId !== null && requestId !== homeRequestIdRef.current) {
        return;
      }

      if (merge) {
        setFollowingMap((prev) => ({ ...prev, ...incoming }));
      } else {
        setFollowingMap(incoming);
      }
    } catch (error) {
      console.error('Error fetching batch following status:', error);
      if (!merge) {
        setFollowingMap({});
      }
    }
  };

  // Handle follow/unfollow toggle for feed users
  const handleFeedFollowToggle = async (userId) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    if (userId === user.id) return; // Can't follow yourself

    setFollowLoading(prev => ({ ...prev, [userId]: true }));
    
    try {
      const isCurrentlyFollowing = followingMap[userId] || false;
      
      if (isCurrentlyFollowing) {
        // Unfollow
        await api.delete(`/follow/${userId}`);
        setFollowingMap(prev => ({ ...prev, [userId]: false }));
        trackFollow('user', userId, false);
      } else {
        // Follow
        await api.post(`/follow/${userId}`);
        setFollowingMap(prev => ({ ...prev, [userId]: true }));
        trackFollow('user', userId, true);
      }
      
    } catch (error) {
      console.error('Error toggling follow:', error);
      alert('Failed to update follow status. Please try again.');
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  const fetchFeed = async (options = {}) => {
    try {
      if (!user) return;

      const requestId = (homeRequestIdRef.current += 1);
      const showLoading = options.showLoading === true;
      if (showLoading) {
        setLoading(true);
      }

      // Reserve space / show "module loading" UIs on first content paint (but don't block it).
      setFeaturedReviewLoading(true);
      setTopReviewsLoading(true);

      // Critical path: the recent feed gates first content.
      const pageLimit = 20;
      const response = await api.get('/feed', { params: { images_only: true, limit: pageLimit } });
      if (requestId !== homeRequestIdRef.current) return;

      const rawFeed = response.data.feed || [];
      const nextCursor = response.data.next_cursor ?? null;
      const hasMore =
        typeof response.data.has_more === 'boolean' ? response.data.has_more : rawFeed.length >= pageLimit;

      setFeed(rawFeed);
      setFeedCursor(nextCursor);
      setFeedHasMore(hasMore);
      feedCursorRef.current = nextCursor;
      feedHasMoreRef.current = hasMore;
      feedLoadingMoreRef.current = false;
      setFeedLoadingMore(false);

      // Unblock Home render as soon as /api/feed is ready.
      setLoading(false);

      // Best-effort work: defer to next tick so the feed can paint first.
      if (deferredWorkTimeoutRef.current) {
        clearTimeout(deferredWorkTimeoutRef.current);
        deferredWorkTimeoutRef.current = null;
      }

      deferredWorkTimeoutRef.current = window.setTimeout(() => {
        deferredWorkTimeoutRef.current = null;
        if (!user) return;
        if (requestId !== homeRequestIdRef.current) return;

        // Preload the carousel chunk after first paint (keeps react-slick off the critical path).
        import('../components/TopReviewsCarousel').catch(() => {});

        // Following status + WichList membership are "nice-to-have" for initial render.
        fetchBatchFollowingStatus(rawFeed, { requestId }).catch((error) =>
          console.error('Error fetching batch following status:', error)
        );

        const uniqueSandwichIds = [...new Set(rawFeed.map((item) => item?.sandwich?.id).filter(Boolean))];
        if (uniqueSandwichIds.length > 0) {
          Promise.resolve(ensureMembership(uniqueSandwichIds)).catch((error) =>
            console.error('Error ensuring WichList membership:', error)
          );
        }

        // Featured review (best-effort)
        api
          .get('/feed/featured', { params: { images_only: true } })
          .then((featuredResponse) => {
            if (requestId !== homeRequestIdRef.current) return;
            const featured = featuredResponse.data?.featured || null;
            setFeaturedReview(featured);
            setFeaturedExpanded(false); // Reset expanded state for new featured review

            // De-dupe: don't show the featured review again in the recent feed list.
            const featuredId = featured?.review_id;
            if (featuredId) {
              setFeed((prev) => prev.filter((it) => it?.review_id !== featuredId));
            }

            // Merge follow + membership for the featured surface as it arrives.
            if (featured) {
              fetchBatchFollowingStatus([featured], { merge: true, requestId }).catch((error) =>
                console.error('Error fetching featured following status:', error)
              );
              const featuredSandwichId = featured?.sandwich?.id;
              if (featuredSandwichId) {
                Promise.resolve(ensureMembership([featuredSandwichId])).catch((error) =>
                  console.error('Error ensuring featured WichList membership:', error)
                );
              }
            }
          })
          .catch((error) => console.error('Error fetching featured review:', error))
          .finally(() => {
            if (requestId !== homeRequestIdRef.current) return;
            setFeaturedReviewLoading(false);
          });

        // Trending sandwiches (best-effort)
        api
          .get('/trending/sandwiches', { params: { limit: 20 } })
          .then((trendingResponse) => {
            if (requestId !== homeRequestIdRef.current) return;
            const trendingSandwiches = trendingResponse.data?.sandwiches || [];
            setTopReviews(trendingSandwiches);
          })
          .catch((error) => console.error('Error fetching trending sandwiches:', error))
          .finally(() => {
            if (requestId !== homeRequestIdRef.current) return;
            setTopReviewsLoading(false);
          });
      }, 0);
    } catch (error) {
      console.error('Error fetching feed:', error);
      setFeaturedReviewLoading(false);
      setTopReviewsLoading(false);
      setLoading(false);
    }
  };

  const fetchMoreFeed = async () => {
    if (!user) return;
    if (loading) return; // prioritize the initial page load
    if (feedLoadingMoreRef.current) return;
    if (!feedHasMoreRef.current) return;
    const cursorToUse = feedCursorRef.current;
    if (!cursorToUse) return;

    feedLoadingMoreRef.current = true;
    setFeedLoadingMore(true);

    try {
      const pageLimit = 20;
      let attempts = 0;
      let cursor = cursorToUse;

      // With images_only paging, it's possible to hit a window with no images.
      // We allow a couple retries (advancing the cursor) so users don't get stuck.
      while (attempts < 3 && cursor) {
        const response = await api.get('/feed', {
          params: { images_only: true, limit: pageLimit, cursor }
        });

        const nextFeed = response.data.feed || [];
        const nextCursor = response.data.next_cursor ?? null;
        const hasMore = !!response.data.has_more;

        if (nextFeed.length > 0) {
          setFeed((prev) => {
            const seen = new Set(prev.map((it) => it.review_id));
            const featuredId = featuredReview?.review_id;
            const deduped = nextFeed.filter(
              (it) => it?.review_id && !seen.has(it.review_id) && it.review_id !== featuredId
            );
            return deduped.length > 0 ? [...prev, ...deduped] : prev;
          });

          // Merge following state and WichList membership for newly loaded rows
          await fetchBatchFollowingStatus(nextFeed, { merge: true });
          const uniqueSandwichIds = [
            ...new Set(nextFeed.map((item) => item?.sandwich?.id).filter(Boolean))
          ];
          if (uniqueSandwichIds.length > 0) {
            await ensureMembership(uniqueSandwichIds);
          }
        }

        setFeedCursor(nextCursor);
        setFeedHasMore(hasMore);
        feedCursorRef.current = nextCursor;
        feedHasMoreRef.current = hasMore;

        if (nextFeed.length > 0 || !hasMore || !nextCursor) {
          break;
        }

        cursor = nextCursor;
        attempts += 1;
      }
    } catch (error) {
      console.error('Error fetching more feed:', error);
    } finally {
      feedLoadingMoreRef.current = false;
      setFeedLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    if (loading) return;
    if (!feedHasMore) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const shouldLoad = entries.some((entry) => entry.isIntersecting);
        if (shouldLoad) {
          fetchMoreFeed();
        }
      },
      // Prefetch before the user actually hits the bottom
      { root: null, rootMargin: '1000px 0px', threshold: 0.01 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [user, loading, feedHasMore]);

  // Detect which review texts are actually overflowing (truncated by line-clamp)
  useEffect(() => {
    const checkOverflow = () => {
      // Check feed review texts
      const newOverflowing = {};
      Object.entries(reviewTextRefs.current).forEach(([reviewId, el]) => {
        if (el && !expandedReviews[reviewId]) {
          // Text is overflowing if scrollHeight > clientHeight
          newOverflowing[reviewId] = el.scrollHeight > el.clientHeight;
        }
      });
      setOverflowingReviews(prev => {
        // Only update if changed to prevent infinite loops
        const hasChanges = Object.keys(newOverflowing).some(
          key => prev[key] !== newOverflowing[key]
        );
        return hasChanges ? { ...prev, ...newOverflowing } : prev;
      });
      
      // Check featured review text
      if (featuredTextRef.current && !featuredExpanded) {
        const isOverflowing = featuredTextRef.current.scrollHeight > featuredTextRef.current.clientHeight;
        setFeaturedOverflowing(isOverflowing);
      }
    };
    
    // Check after render and on window resize
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [feed, expandedReviews, featuredReview, featuredExpanded]);

  const handleLike = async (reviewId, currentlyLiked) => {
    // Trigger animation immediately for responsive feel
    const animationType = currentlyLiked ? 'unlike' : 'like';
    setLikeAnimating(prev => ({ ...prev, [reviewId]: animationType }));
    
    // Clear animation after it completes
    setTimeout(() => {
      setLikeAnimating(prev => ({ ...prev, [reviewId]: null }));
    }, 600);

    // Optimistic update: update UI immediately before API call
    const nextLiked = !currentlyLiked;
    
    // Helper to update like state in review-feed items
    const updateReviewLikes = (items) =>
      items.map(item =>
        item.review_id === reviewId
          ? {
              ...item,
              liked_by_me: nextLiked,
              likes_count: Math.max(0, (item.likes_count || 0) + (nextLiked ? 1 : -1))
            }
          : item
      );
    
    // Update Home surfaces optimistically (feed + featured review)
    setFeed(prev => updateReviewLikes(prev));
    setFeaturedReview(prev =>
      prev?.review_id === reviewId
        ? {
            ...prev,
            liked_by_me: nextLiked,
            likes_count: Math.max(0, (prev.likes_count || 0) + (nextLiked ? 1 : -1))
          }
        : prev
    );

    try {
      const response = await api.post(`/like/${reviewId}`);
      trackLike(reviewId, response.data.liked);
      // No need to fetchFeed() - we already updated optimistically
    } catch (error) {
      console.error('Error liking review:', error);
      // Rollback on error
      const rollbackReviewLikes = (items) =>
        items.map(item =>
          item.review_id === reviewId
            ? {
                ...item,
                liked_by_me: currentlyLiked,
                likes_count: Math.max(0, (item.likes_count || 0) + (currentlyLiked ? 1 : -1))
              }
            : item
        );
      setFeed(prev => rollbackReviewLikes(prev));
      setFeaturedReview(prev =>
        prev?.review_id === reviewId
          ? {
              ...prev,
              liked_by_me: currentlyLiked,
              likes_count: Math.max(0, (prev.likes_count || 0) + (currentlyLiked ? 1 : -1))
            }
          : prev
      );
    }
  };

  const handleSandwichClick = (sandwich, restaurant) => {
    trackSandwichView(sandwich.id, sandwich.name, restaurant.name);
  };

  const handleAddToList = (sandwich, options = {}) => {
    setSelectedSandwich({
      id: sandwich.id,
      name: sandwich.name,
      reviewId: options.reviewId || null
    });
    setShowAddToListModal(true);
  };

  const handleShare = (review) => {
    setSelectedReview(review);
    setShowShareModal(true);
  };

  const shareReview = async (shareMethod) => {
    if (!selectedReview) return;

    const shareContent = generateShareContent(selectedReview);
    const isAbortError = (error) =>
      error?.name === 'AbortError' ||
      (typeof error?.message === 'string' && error.message.toLowerCase().includes('abort'));

    if (shareMethod === 'copy_link') {
      try {
        await navigator.clipboard.writeText(shareContent.url);
      } catch (error) {
        console.error('Error copying review link:', error);
        return;
      }
    } else if (shareMethod === 'native_share') {
      if (!navigator.share) {
        // Should be unreachable because the modal hides this option, but keep it safe.
        try {
          await navigator.clipboard.writeText(shareContent.url);
        } catch (error) {
          console.error('Error copying review link fallback:', error);
        }
        return;
      }

      try {
        await navigator.share({
          title: shareContent.title,
          text: shareContent.text,
          url: shareContent.url
        });
      } catch (error) {
        // User cancelled share sheet (common on iOS Safari) – don't show an error.
        if (isAbortError(error)) return;
        console.error('Error sharing review:', error);
        return;
      }
    } else {
      console.warn('Unknown share method:', shareMethod);
      return;
    }

    // Best-effort tracking AFTER the share/copy action to preserve iOS “user gesture” requirements.
    api
      .post(`/share/${selectedReview.review_id}`, { share_method: shareMethod })
      .catch((error) => console.error('Error tracking share:', error));

    fetchFeed();
    setShowShareModal(false);
  };

  const generateShareContent = (review) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const deepLink = `${origin}/review/${review.review_id}`;
    const baseText = `Check out this ${formatRating(review.rating)}⭐ sandwich review! "${review.sandwich.name}" at ${review.restaurant.name} by @${review.user.username}`;
    const fullText = review.review_text 
      ? `${baseText}: "${review.review_text.substring(0, 100)}${review.review_text.length > 100 ? '...' : ''}"`
      : baseText;

    return {
      title: 'Check out this sandwich!',
      text: fullText,
      url: deepLink
    };
  };

  const handleEditReview = (review) => {
    setReviewToEdit(review);
    setShowEditModal(true);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-hearth-tan">
      {/* Hero Section */}
      <div className="relative bg-hearth-tan">
        <div className={"max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 " + (user ? "py-4 sm:py-6 lg:py-8" : "py-8 sm:py-12 lg:py-20") }>
          <div className={"grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center " + (user ? "min-h-[320px] sm:min-h-[420px]" : "min-h-[400px] sm:min-h-[500px]") }>
            {/* Featured Review / Hero Image */}
            <div className="order-1 lg:order-1">
              {isGuest ? (
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1729501207780-ff45fbf560d0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwZWF0aW5nJTIwc2FuZHdpY2h8ZW58MHx8fHwxNzQ5NzQ2OTI5fDA&ixlib=rb-4.1.0&q=85"
                    alt="Friends sharing artisanal sandwiches at a sunny picnic table"
                    className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : featuredReview ? (
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Link
                      to={`/profile/${featuredReview.user.id}`}
                      className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:ring-2 hover:ring-passion-red hover:ring-opacity-50 transition-all"
                    >
                      {featuredReview.user.image_url ? (
                        <img 
                          src={formatImageSrc(featuredReview.user.image_url)}
                          alt={featuredReview.user.name} 
                          className="w-10 h-10 rounded-full object-cover" 
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <span className="text-gray-500 font-semibold text-sm">
                          {featuredReview.user.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </Link>
                    <div className="flex-1">
                      <Link
                        to={`/profile/${featuredReview.user.id}`}
                        className="font-semibold text-text-black hover:text-passion-red transition-colors"
                      >
                        {featuredReview.user.name}
                      </Link>
                    </div>
                    {user && user.id !== featuredReview.user.id && (
                      <button
                        onClick={() => handleFeedFollowToggle(featuredReview.user.id)}
                        disabled={followLoading[featuredReview.user.id]}
                        className={`follow-btn px-3 py-1 rounded-full text-xs font-medium transition-colors flex-shrink-0 ${
                          followingMap[featuredReview.user.id]
                            ? 'bg-white border border-passion-red text-passion-red hover:bg-red-50'
                            : 'bg-passion-red text-white hover:bg-red-600'
                        } ${followLoading[featuredReview.user.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {followLoading[featuredReview.user.id] ? (
                          followingMap[featuredReview.user.id] ? 'Unfollowing...' : 'Following...'
                        ) : (
                          followingMap[featuredReview.user.id] ? 'Following' : 'Follow'
                        )}
                      </button>
                    )}
                  </div>
                  
                  {featuredReview.image_url && (
                    <Link
                      to={`/sandwich/${featuredReview.sandwich.id}`}
                      onClick={() => handleSandwichClick(featuredReview.sandwich, featuredReview.restaurant)}
                      className="block mb-4 aspect-[4/3] overflow-hidden rounded-lg"
                    >
                      <img
                        src={featuredReview.image_url}
                        alt="Featured sandwich"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </Link>
                  )}
                  
                  <div className="flex items-center justify-between mb-2">
                    <Link
                      to={`/sandwich/${featuredReview.sandwich.id}`}
                      className="text-lg font-semibold text-passion-red hover:underline line-clamp-1 flex-1 mr-2"
                    >
                      {featuredReview.sandwich.name}
                    </Link>
                    {user && (
                      <WantToTryButton
                        sandwichId={featuredReview.sandwich.id}
                        variant="prominent"
                        sourceReviewId={featuredReview.review_id}
                        source="home_featured"
                        onFeedback={(nextState) => showWichListFeedback(nextState, featuredReview.sandwich.name)}
                      />
                    )}
                  </div>
                  
                  <p className="text-gray-600 text-xs sm:text-sm mb-2">
                    @ {featuredReview.restaurant.name}, {featuredReview.restaurant.city}, {featuredReview.restaurant.state}
                  </p>
                  
                  <div className="mb-3">
                    <StarRating value={featuredReview.rating} sizeClassName="text-lg" />
                  </div>
                  
                  {featuredReview.review_text && (
                    <div>
                      <p 
                        ref={featuredTextRef}
                        className={`text-gray-800 text-sm leading-relaxed ${
                          !featuredExpanded ? 'line-clamp-3' : ''
                        }`}
                      >
                        {featuredReview.review_text}
                      </p>
                      {(featuredOverflowing || featuredExpanded) && (
                        <button
                          onClick={() => setFeaturedExpanded(prev => !prev)}
                          className="text-passion-red text-sm font-medium hover:underline mt-1"
                        >
                          {featuredExpanded ? 'Less' : 'More'}
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Action buttons */}
                  <div className="flex items-center space-x-4 mt-4 text-gray-500">
                    <button
                      onClick={() => handleLike(featuredReview.review_id, featuredReview.liked_by_me)}
                      className={`cs-like-button flex items-center space-x-1 transition-colors ${
                        featuredReview.liked_by_me ? 'text-passion-red' : 'hover:text-passion-red'
                      } ${likeAnimating[featuredReview.review_id] === 'like' ? 'cs-like-animating' : ''} ${likeAnimating[featuredReview.review_id] === 'unlike' ? 'cs-unlike-animating' : ''}`}
                      aria-label={`Like review, ${formatCount(featuredReview.likes_count)} likes`}
                    >
                      <Heart size={20} fill={featuredReview.liked_by_me ? "currentColor" : "none"} aria-hidden="true" />
                      <span className="text-sm">{formatCount(featuredReview.likes_count)}</span>
                    </button>
                    <Link
                      to={`/sandwich/${featuredReview.sandwich.id}#comments-${featuredReview.review_id}`}
                      className="flex items-center space-x-1 hover:text-passion-red transition-colors"
                      aria-label={`${formatCount(featuredReview.comments_count || 0)} comments`}
                    >
                      <MessageCircle size={19} aria-hidden="true" />
                      <span className="text-sm">{formatCount(featuredReview.comments_count || 0)}</span>
                    </Link>
                    <button
                      onClick={() => handleShare(featuredReview)}
                      className="flex items-center space-x-1 hover:text-passion-red transition-colors"
                      aria-label="Share review"
                    >
                      <Share size={19} aria-hidden="true" />
                    </button>
                    {user && (
                      <button
                        onClick={() => handleAddToList(featuredReview.sandwich, { reviewId: featuredReview.review_id })}
                        className="flex items-center space-x-1 hover:text-passion-red transition-colors"
                        aria-label="Add to list"
                      >
                        <Plus size={20} aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
              ) : featuredReviewLoading ? (
                <div className="bg-white rounded-lg shadow-lg p-6 animate-pulse">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                    </div>
                  </div>
                  <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-lg p-6 text-center">
                  <p className="text-gray-600 mb-4">No reviews yet!</p>
                  <Link
                    to="/upload"
                    className="inline-flex items-center bg-passion-red text-white px-6 py-3 rounded-full hover:bg-red-600 transition-colors"
                  >
                    Be the first to share →
                  </Link>
                </div>
              )}
            </div>
            
            {/* Journey Copy & CTA */}
            {isGuest ? (
              <div className="order-2 lg:order-2 text-center flex flex-col justify-center h-full py-4 lg:py-8">
                <h1 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl font-bold text-text-black gayathri-font mb-4 sm:mb-6 lg:mb-8 leading-tight">
                  Join Us on the Journey to<br />
                  <span className="text-passion-red">1 Million Sandwiches</span>
                </h1>
                
                <div className="space-y-2 sm:space-y-3 lg:space-y-4 mb-6 sm:mb-8 lg:mb-10 mplus2-font text-sm sm:text-base lg:text-lg text-gray-700">
                  <div className="text-center">
                    <span className="font-semibold text-passion-red">Log and review</span> the sandwiches you're eating.
                  </div>
                  <div className="text-center">
                    <span className="font-semibold text-passion-red">Discover</span> new sandwiches to add to your <span className="font-semibold">WichList</span>.
                  </div>
                  <div className="text-center">
                    <span className="font-semibold text-passion-red">Connect</span> with fellow sandwich enthusiasts.
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                  <Link
                    to="/upload"
                    className="inline-flex items-center justify-center bg-passion-red text-white px-3 sm:px-8 py-3 sm:py-4 rounded-[64px] hover:bg-red-600 transition-colors text-base sm:text-lg mplus2-font font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
                  >
                    Upload Your Sandwich →
                  </Link>
                  <button
                    type="button"
                    onClick={handleExploreClick}
                    className="inline-flex items-center justify-center border-2 border-passion-red text-passion-red px-3 sm:px-8 py-3 sm:py-4 rounded-[64px] hover:bg-passion-red hover:text-white transition-colors text-base sm:text-lg mplus2-font font-semibold"
                  >
                    Explore Sandwiches
                  </button>
                </div>
                
                {/* Legal Consent Copy */}
                <p className="mt-6 text-xs sm:text-sm text-gray-500 text-center mplus2-font">
                  By continuing, you agree to our{' '}
                  <Link to="/terms" className="text-passion-red hover:underline">
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-passion-red hover:underline">
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            ) : (
              <div className="order-2 lg:order-2 text-center flex flex-col justify-center h-full py-2 lg:py-4">
                <h1 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl font-bold text-text-black gayathri-font mb-2 sm:mb-3 lg:mb-4 leading-tight">
                  Join Us on the Journey to<br />
                  <span className="text-passion-red">1 Million Sandwiches</span>
                </h1>
                {/* Progress Tracker for logged-in users */}
                <div className="mb-3 sm:mb-4 lg:mb-6">
                  <ProgressTracker />
                </div>
                <div className="mb-3 sm:mb-4 lg:mb-6 w-full">
                  {userStatsLoading ? (
                    <div className="h-16 w-full animate-pulse rounded-2xl bg-white/60" />
                  ) : (
                    <div className="rounded-2xl bg-white/80 p-4 text-left shadow-sm backdrop-blur">
                      <PersonalProgressSummary stats={userStats} />
                    </div>
                  )}
                </div>
                {wichListReviewCount > 0 && (
                  <div className="mb-3 sm:mb-4 lg:mb-6 w-full rounded-2xl bg-white/80 p-3 text-sm text-gray-800 shadow-sm backdrop-blur flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      There {wichListReviewCount === 1 ? 'is' : 'are'} {wichListReviewCount} recent review
                      {wichListReviewCount === 1 ? '' : 's'} on sandwiches in your WichList.
                    </span>
                    <button
                      type="button"
                      onClick={handleActivityStripClick}
                      className="text-sm font-semibold text-passion-red hover:underline"
                    >
                      See them →
                    </button>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                  <Link
                    to="/upload"
                    className="inline-flex items-center justify-center bg-passion-red text-white px-3 sm:px-8 py-3 sm:py-4 rounded-[64px] hover:bg-red-600 transition-colors text-base sm:text-lg mplus2-font font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200"
                  >
                    {personalReviewCount === 0 ? 'Upload Your First Sandwich →' : 'Upload Your Sandwich →'}
                  </Link>
                  <button
                    type="button"
                    onClick={handleExploreClick}
                    className="inline-flex items-center justify-center border-2 border-passion-red text-passion-red px-3 sm:px-8 py-3 sm:py-4 rounded-[64px] hover:bg-passion-red hover:text-white transition-colors text-base sm:text-lg mplus2-font font-semibold"
                  >
                    Explore Sandwiches
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isGuest && (
        <>
          {topReviews.length > 0 ? (
            <Suspense fallback={<TopReviewsCarouselSkeleton />}>
              <TopReviewsCarousel
                reviews={topReviews}
                onWichListFeedback={(nextState, sandwichName) =>
                  showWichListFeedback(nextState, sandwichName)
                }
              />
            </Suspense>
          ) : topReviewsLoading ? (
            <TopReviewsCarouselSkeleton />
          ) : null}
        </>
      )}

      {user && (wichListShelfLoading || wichListShelf.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-2xl sm:text-3xl font-bold text-text-black section-heading-font">From your WichList</h3>
            {wichListShelf.length > 0 && (
              <Link
                to={`/profile/${user.id}?tab=want-to-try`}
                className="text-sm font-semibold text-passion-red hover:underline"
              >
                View all →
              </Link>
            )}
          </div>
          {wichListShelfLoading ? (
            <>
              {/* Mobile skeleton - single card */}
              <div className="md:hidden">
                <div className="rounded-xl bg-white/70 p-4 shadow animate-pulse">
                  <div className="h-32 w-full rounded-lg bg-gray-200 mb-3" />
                  <div className="h-4 w-3/4 rounded bg-gray-200 mb-2" />
                  <div className="h-3 w-1/2 rounded bg-gray-200" />
                </div>
              </div>
              {/* Desktop skeleton - centered cards */}
              <div className="hidden md:flex justify-center gap-4">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="w-[220px] rounded-xl bg-white/70 p-4 shadow animate-pulse">
                    <div className="h-32 w-full rounded-lg bg-gray-200 mb-3" />
                    <div className="h-4 w-3/4 rounded bg-gray-200 mb-2" />
                    <div className="h-3 w-1/2 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Mobile - single card (first item only) */}
              <div className="md:hidden">
                {wichListShelf.length > 0 && (
                  <Link
                    to={`/sandwich/${wichListShelf[0].id}`}
                    className="block rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                    onClick={() => handleSandwichClick(wichListShelf[0], wichListShelf[0].restaurant || {})}
                  >
                    {wichListShelf[0].featured_image_url && (
                      <div className="aspect-[4/3] overflow-hidden rounded-lg">
                        <img
                          src={formatImageSrc(wichListShelf[0].featured_image_url)}
                          alt={wichListShelf[0].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2">{wichListShelf[0].name}</p>
                      {wichListShelf[0].restaurant && (
                        <p className="text-xs text-gray-600 line-clamp-1 mt-1">
                          @ {wichListShelf[0].restaurant.name}, {wichListShelf[0].restaurant.city}, {wichListShelf[0].restaurant.state}
                        </p>
                      )}
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-600">
                        {wichListShelf[0].average_rating > 0 && (
                          <>
                            <StarRating value={wichListShelf[0].average_rating} sizeClassName="text-xs" />
                            <span>{Number(wichListShelf[0].average_rating).toFixed(1)}</span>
                          </>
                        )}
                        {wichListShelf[0].review_count > 0 && (
                          <span>
                            ({wichListShelf[0].review_count} {wichListShelf[0].review_count === 1 ? 'review' : 'reviews'})
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                )}
              </div>

              {/* Desktop - adaptive grid that scales with item count */}
              <div className="hidden md:block">
                <div className={`grid gap-4 ${
                  wichListShelf.length === 1 
                    ? 'grid-cols-1 max-w-md mx-auto' 
                    : wichListShelf.length === 2 
                      ? 'grid-cols-2 max-w-2xl mx-auto'
                      : wichListShelf.length === 3 
                        ? 'grid-cols-3 max-w-4xl mx-auto'
                        : 'md:grid-cols-4 xl:grid-cols-5'
                }`}>
                  {wichListShelf.slice(0, 5).map((sandwich, idx) => (
                    <Link
                      key={sandwich.id}
                      to={`/sandwich/${sandwich.id}`}
                      className={`rounded-xl border border-gray-200 bg-white/80 p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                        wichListShelf.length >= 4 && idx === 4 ? 'hidden xl:block' : ''
                      }`}
                      onClick={() => handleSandwichClick(sandwich, sandwich.restaurant || {})}
                    >
                      {sandwich.featured_image_url && (
                        <div className="aspect-[4/3] overflow-hidden rounded-lg">
                          <img
                            src={formatImageSrc(sandwich.featured_image_url)}
                            alt={sandwich.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="mt-3">
                        <p className="text-base font-semibold text-gray-900 line-clamp-2">{sandwich.name}</p>
                        {sandwich.restaurant && (
                          <p className="text-sm text-gray-600 line-clamp-1 mt-1">
                            @ {sandwich.restaurant.name}, {sandwich.restaurant.city}, {sandwich.restaurant.state}
                          </p>
                        )}
                        <div className="mt-2 flex items-center gap-1 text-sm text-gray-600">
                          {sandwich.average_rating > 0 && (
                            <>
                              <StarRating value={sandwich.average_rating} sizeClassName="text-sm" />
                              <span>{Number(sandwich.average_rating).toFixed(1)}</span>
                            </>
                          )}
                          {sandwich.review_count > 0 && (
                            <span>
                              ({sandwich.review_count} {sandwich.review_count === 1 ? 'review' : 'reviews'})
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {/* Feed Section */}
      <div id={user ? 'feed-section' : undefined} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {user && (
          <>
            <h2 className="text-2xl sm:text-3xl font-bold text-text-black section-heading-font mb-6 sm:mb-8">Recent Reviews</h2>
            {feed.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 mplus2-font text-lg">
                  No reviews yet. Be the first to share a sandwich!
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-6">
                {feed.map((item) => {
                  const isWichListItem = isInWichList(item.sandwich.id);
                  return (
                    <div
                      key={item.review_id}
                      className={`bg-white rounded-lg shadow-lg p-4 sm:p-6 relative transition w-full max-w-2xl ${
                        highlightWichListReviews && isWichListItem ? 'ring-2 ring-passion-red/40' : ''
                      }`}
                    >
                    {/* Three-dot menu for own posts */}
                    {user && user.id === item.user.id && (
                  <div className="absolute top-2 right-2 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowListMenu(showListMenu === item.review_id ? null : item.review_id);
                      }}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-white transition-colors"
                      aria-label="Review options"
                    >
                      <MoreVertical size={20} aria-hidden="true" />
                    </button>
                    {showListMenu === item.review_id && (
                      <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border py-2 min-w-[120px] z-20 list-menu">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditReview(item);
                            setShowListMenu(null);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Edit2 size={16} aria-hidden="true" />
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center gap-3 sm:gap-4 mb-4">
                  <Link
                    to={`/profile/${item.user.id}`}
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-passion-red hover:ring-opacity-50 transition-all"
                  >
                    {item.user.image_url ? (
                      <img 
                        src={formatImageSrc(item.user.image_url)}
                        alt={item.user.name} 
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover" 
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span className="text-gray-500 font-semibold text-sm sm:text-base">
                        {item.user.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center min-w-0 gap-2">
                      <Link
                        to={`/profile/${item.user.id}`}
                        className="font-semibold text-text-black text-sm sm:text-base hover:text-passion-red transition-colors truncate"
                      >
                        {item.user.name}
                      </Link>
                      <span className="text-gray-400 flex-shrink-0">•</span>
                      <span className="text-gray-500 text-xs sm:text-sm flex-shrink-0 whitespace-nowrap">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                      {item.is_edited && (
                        <span
                          className="text-gray-400 text-xs sm:text-sm flex-shrink-0 whitespace-nowrap"
                          title={`Last edited: ${new Date(item.edited_at).toLocaleDateString()}`}
                        >
                          (edited)
                        </span>
                      )}
                    </div>
                  </div>
                  {user && user.id !== item.user.id && (
                    <button
                      onClick={() => handleFeedFollowToggle(item.user.id)}
                      disabled={followLoading[item.user.id]}
                      className={`follow-btn flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        followingMap[item.user.id]
                          ? 'bg-white border border-passion-red text-passion-red hover:bg-red-50'
                          : 'bg-passion-red text-white hover:bg-red-600'
                      } ${followLoading[item.user.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {followLoading[item.user.id] ? (
                        followingMap[item.user.id] ? 'Unfollowing...' : 'Following...'
                      ) : (
                        followingMap[item.user.id] ? 'Following' : 'Follow'
                      )}
                    </button>
                  )}
                </div>
                
                <div className="flex items-start justify-between gap-3 mb-1">
                  <Link
                    to={`/sandwich/${item.sandwich.id}`}
                    className="text-base sm:text-lg font-semibold text-passion-red hover:underline flex-1 line-clamp-2"
                    onClick={(e) => {
                      handleSandwichClick(item.sandwich, item.restaurant);
                    }}
                  >
                    {item.sandwich.name}
                  </Link>
                  {user && (
                    <WantToTryButton
                      sandwichId={item.sandwich.id}
                      size={20}
                      variant="prominent"
                      sourceReviewId={item.review_id}
                      source="feed"
                      onFeedback={(nextState) => showWichListFeedback(nextState, item.sandwich.name)}
                    />
                  )}
                </div>
                <p className="text-gray-600 text-xs sm:text-sm mb-2">
                  @ {item.restaurant.name}, {item.restaurant.city}, {item.restaurant.state}
                </p>
                
                <div className="flex items-center gap-2 my-2">
                  <StarRating value={item.rating} sizeClassName="text-base sm:text-lg" />
                  <span className="text-gray-600 text-sm sm:text-base">({formatRating(item.rating)}/5)</span>
                </div>
                
                {item.review_text && (
                  <div className="mb-3">
                    <p 
                      ref={el => { if (el) reviewTextRefs.current[item.review_id] = el; }}
                      className={`text-gray-800 text-sm sm:text-base leading-relaxed ${
                        !expandedReviews[item.review_id] ? 'line-clamp-3' : ''
                      }`}
                    >
                      {item.review_text}
                    </p>
                    {(overflowingReviews[item.review_id] || expandedReviews[item.review_id]) && (
                      <div className="flex justify-end">
                        <button
                          onClick={() => setExpandedReviews(prev => ({
                            ...prev,
                            [item.review_id]: !prev[item.review_id]
                          }))}
                          className="text-passion-red text-sm font-medium hover:underline mt-1"
                        >
                          {expandedReviews[item.review_id] ? 'Less' : 'More'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {item.image_url && (
                  <Link
                    to={`/sandwich/${item.sandwich.id}`}
                    onClick={() => handleSandwichClick(item.sandwich, item.restaurant)}
                    className="block mb-3 aspect-[4/3] overflow-hidden rounded-lg"
                  >
                    <img
                      src={item.image_url}
                      alt="Sandwich"
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </Link>
                )}
                
                {/* Social Actions */}
                <div className="flex items-center space-x-2 text-gray-500 text-sm sm:text-base">
                  <button
                    onClick={() => handleLike(item.review_id, item.liked_by_me)}
                    className={`cs-like-button flex items-center space-x-1 transition-colors py-2 ${
                      item.liked_by_me ? 'text-passion-red' : 'hover:text-passion-red'
                    } ${likeAnimating[item.review_id] === 'like' ? 'cs-like-animating' : ''} ${likeAnimating[item.review_id] === 'unlike' ? 'cs-unlike-animating' : ''}`}
                    aria-label={`Like review, ${formatCount(item.likes_count)} likes`}
                  >
                    <Heart size={20} fill={item.liked_by_me ? "currentColor" : "none"} aria-hidden="true" />
                    <span>{formatCount(item.likes_count)}</span>
                  </button>
                  <button
                    onClick={() => toggleCommentThread(item.review_id)}
                    className="flex items-center space-x-1 hover:text-passion-red transition-colors py-2"
                    aria-label={`${formatCount(item.comments_count || 0)} comments`}
                  >
                    <MessageCircle size={19} aria-hidden="true" />
                    <span>{formatCount(item.comments_count || 0)}</span>
                  </button>
                  <button
                    onClick={() => handleShare(item)}
                    className="flex items-center space-x-1 hover:text-passion-red transition-colors py-2"
                    aria-label={`Share review, ${formatCount(item.share_count || 0)} shares`}
                  >
                    <Share size={19} aria-hidden="true" />
                    <span>{formatCount(item.share_count || 0)}</span>
                  </button>
                  {user && (
                    <button
                      onClick={() => handleAddToList(item.sandwich, { reviewId: item.review_id })}
                      className="flex items-center space-x-1 hover:text-passion-red transition-colors py-2"
                      aria-label="Add to list"
                    >
                      <Plus size={20} aria-hidden="true" />
                    </button>
                  )}
                </div>
                
                {/* Comment Thread */}
                {commentThreads[item.review_id] && (
                  <div className="mt-4 ml-4 border-l-2 border-gray-200 pl-4">
                    {commentLoading[item.review_id] ? (
                      <div className="text-sm text-gray-500">Loading comments...</div>
                    ) : (
                      <div className="relative">
                        {/* Scrollable Comments Container */}
                        <div className="comment-thread-container comment-thread-fade max-h-80 overflow-y-auto pr-2 mb-4">
                          {commentThreads[item.review_id].length === 0 ? (
                            <div className="text-sm text-gray-500 py-4">Be the first to comment</div>
                          ) : (
                            <div className="space-y-3 pb-2">
                              {commentThreads[item.review_id].map((comment) => (
                                <div key={comment.id} className="flex items-start space-x-3">
                                  <Link
                                    to={`/profile/${comment.user?.id}`}
                                    className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-1 hover:ring-2 hover:ring-passion-red hover:ring-opacity-50 transition-all"
                                  >
                                    {comment.user?.image_url ? (
                                      <img 
                                        src={formatImageSrc(comment.user.image_url)} 
                                        alt={comment.user.name} 
                                        className="w-6 h-6 rounded-full object-cover" 
                                      />
                                    ) : (
                                      <span className="text-gray-500 font-semibold text-xs">
                                        {comment.user?.name?.charAt(0).toUpperCase() || 'U'}
                                      </span>
                                    )}
                      </Link>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                                      <Link
                                        to={`/profile/${comment.user?.id}`}
                                        className="font-medium hover:text-passion-red transition-colors"
                                      >
                                        {comment.user?.name || 'Unknown User'}
                                      </Link>
                                      <span>•</span>
                                      <span>{new Date(comment.created_at).toLocaleDateString()}</span>
                                      {user && comment.user?.id === user.id && (
                                        <>
                                          <span>•</span>
                                          <button
                                            type="button"
                                            onClick={() => handleDeleteComment(item.review_id, comment.id)}
                                            className="text-gray-400 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded-sm"
                                          >
                                            Delete
                                          </button>
                                        </>
                                      )}
                    </div>
                                    <p className="text-sm text-gray-800 mt-1 break-words">{comment.comment_text}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Sticky Comment Form */}
                        <div className="border-t border-gray-100 pt-3">
                          {user ? (
                            <div className="flex items-start space-x-3">
                              <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 mt-2">
                                {user?.image_url ? (
                                  <img 
                                    src={formatImageSrc(user.image_url)} 
                                    alt={user?.name} 
                                    className="w-6 h-6 rounded-full object-cover" 
                                  />
                                ) : (
                                  <span className="text-gray-500 font-semibold text-xs">
                                    {user.name?.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1">
                                <textarea
                                  value={commentText[item.review_id] || ''}
                                  onChange={(e) => handleCommentTextChange(item.review_id, e.target.value)}
                                  placeholder="Add a comment..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-passion-red focus:border-transparent text-sm resize-none"
                                  rows="2"
                                />
                                <div className="flex justify-end mt-2">
                                  <button
                                    onClick={() => submitComment(item.review_id)}
                                    disabled={commentSubmitting[item.review_id] || !commentText[item.review_id]?.trim()}
                                    className="px-4 py-2 bg-passion-red text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                  >
                                    {commentSubmitting[item.review_id] ? 'Posting...' : 'Post'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500 text-center py-2">
                              <Link to="/login" className="text-passion-red hover:underline">
                                Sign in to comment
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                    )}
                  </div>
                  );
                })}

                <div
                  ref={loadMoreSentinelRef}
                  className="h-1 w-full max-w-2xl"
                  aria-hidden="true"
                />
                {feedLoadingMore && (
                  <div className="w-full max-w-2xl text-center text-sm text-gray-500 py-2">
                    Loading more reviews...
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Add to List Modal */}
      {selectedSandwich && (
        <AddToListModal
          isOpen={showAddToListModal}
          onClose={() => {
            setShowAddToListModal(false);
            setSelectedSandwich(null);
          }}
          sandwichId={selectedSandwich.id}
          sandwichName={selectedSandwich.name}
      sourceReviewId={selectedSandwich.reviewId}
        />
      )}
      
      {/* Share Modal */}
      {selectedReview && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedReview(null);
          }}
          onShare={shareReview}
          review={selectedReview}
        />
      )}
      
      {/* Edit Modal */}
      {reviewToEdit && (
        <EditReviewModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setReviewToEdit(null);
          }}
          review={reviewToEdit}
          onSave={fetchFeed}
        />
      )}
      
      {/* Floating Action Button (mobile only, authenticated) */}
      <Fab />
      
      {/* Onboarding Wizard */}
      <OnboardingWizardModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        currentUser={user}
      />
    </div>
  );
};

// Helper component for personal progress summary
const PersonalProgressSummary = ({ stats }) => {
  const count = stats?.reviews_count ?? 0;
  const nextMilestone = getNextUserMilestone(count);
  const progressPercent = getUserProgressPercent(count);

  return (
    <div>
      <p className="text-sm font-semibold text-gray-800">
        You've reviewed {count} sandwich{count === 1 ? '' : 'es'}
      </p>
      <div className="mt-3 h-2 w-full rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-passion-red transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <p className="mt-2 text-xs text-gray-600">
        {count === 0
          ? 'Upload your first sandwich review to start your journey.'
          : nextMilestone
            ? `Review ${nextMilestone - count} more sandwich${nextMilestone - count === 1 ? '' : 'es'} to hit ${nextMilestone}.`
            : "You're a sandwich legend. Keep going!"}
      </p>
    </div>
  );
};

export default Home;


